import { useRef, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Save, Eye, ExternalLink, Award, Stethoscope, Languages,
  MapPin, BookOpen, Edit3, Check, Loader2, Lock,
  Image as ImageIcon, Type, Palette, AlignLeft,
  Link as LinkIcon, Upload, Phone, MessageCircle, Mail as MailIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { getErrorMessage } from '@/lib/errors';
import { getInitials } from '@/lib/utils';
import {
  WebTemplatePreview, DEFAULT_WEB_CONFIG, TIPOGRAFIAS,
  type WebConfig, type TipografiaKey,
} from '@/components/red/WebTemplatePreview';

type Tab = 'red' | 'web';
type CtaTipo = 'tel' | 'wa' | 'mail' | 'url';

const inputCls = 'w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 disabled:bg-gray-50 disabled:text-text-secondary';

export function PerfilPublicoPage() {
  const qc = useQueryClient();

  const [tab, setTab] = useState<Tab>('red');
  const [editando, setEditando] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);
  const heroFileRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState({
    bio: '',
    aniosExperiencia: '',
    subespecialidades: '',
    idiomas: '',
    perfilPublicoSlug: '',
  });

  const [webConfig, setWebConfig] = useState<WebConfig>(DEFAULT_WEB_CONFIG);

  // CTA URL descompuesta en tipo + valor para mejor UX
  const [ctaTipo, setCtaTipo] = useState<CtaTipo>('tel');
  const [ctaValor, setCtaValor] = useState('');

  function parseCtaUrl(url: string): { tipo: CtaTipo; valor: string } {
    if (url.startsWith('tel:')) return { tipo: 'tel', valor: url.slice(4) };
    if (url.startsWith('https://wa.me/')) return { tipo: 'wa', valor: url.slice(14) };
    if (url.startsWith('mailto:')) return { tipo: 'mail', valor: url.slice(7) };
    return { tipo: 'url', valor: url };
  }

  function buildCtaUrl(tipo: CtaTipo, valor: string): string {
    if (!valor) return '';
    if (tipo === 'tel') return `tel:${valor}`;
    if (tipo === 'wa') return `https://wa.me/${valor.replace(/[^0-9]/g, '')}`;
    if (tipo === 'mail') return `mailto:${valor}`;
    return valor;
  }

  const { data: perfil, isLoading } = useQuery({
    queryKey: ['mi-perfil'],
    queryFn: () => api.get('/api/red/mi-perfil').then(r => r.data),
  });

  useEffect(() => {
    if (!perfil) return;
    setForm({
      bio: perfil.bio ?? '',
      aniosExperiencia: perfil.aniosExperiencia?.toString() ?? '',
      subespecialidades: perfil.subespecialidades ?? '',
      idiomas: perfil.idiomas ?? '',
      perfilPublicoSlug: perfil.perfilPublicoSlug ?? '',
    });

    // Cargar webConfig guardado
    if (perfil.perfilPublicoContenido) {
      try {
        const parsed = JSON.parse(perfil.perfilPublicoContenido);
        // Solo cargar si parece un webConfig (no diseño Unlayer viejo)
        if (parsed.colorAcento || parsed.tipografia) {
          setWebConfig({ ...DEFAULT_WEB_CONFIG, ...parsed });
          if (parsed.ctaUrl) {
            const { tipo, valor } = parseCtaUrl(parsed.ctaUrl);
            setCtaTipo(tipo);
            setCtaValor(valor);
          }
        }
      } catch { /* ignorar */ }
    }
  }, [perfil]);

  const updatePerfilMutation = useMutation({
    mutationFn: (dto: any) => api.patch('/api/red/mi-perfil', dto).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mi-perfil'] });
      toast.success('Perfil actualizado');
      setEditando(false);
    },
    onError: (err: any) => {
      toast.error(getErrorMessage(err, 'Error al actualizar el perfil'));
    },
  });

  function handleGuardarDatos(e: React.FormEvent) {
    e.preventDefault();
    updatePerfilMutation.mutate({
      bio: form.bio,
      aniosExperiencia: form.aniosExperiencia ? parseInt(form.aniosExperiencia) : null,
      subespecialidades: form.subespecialidades,
      idiomas: form.idiomas,
      ...(!perfil?.perfilPublicoSlug && form.perfilPublicoSlug
        ? { perfilPublicoSlug: form.perfilPublicoSlug }
        : {}),
    });
  }

  function handleGuardarWeb() {
    updatePerfilMutation.mutate({
      perfilPublicoContenido: JSON.stringify(webConfig),
    });
  }

  async function handleHeroUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !perfil) return;
    setUploadingHero(true);
    try {
      const fd = new FormData();
      fd.append('file', file, file.name);
      const params = new URLSearchParams({
        clinicaId: perfil.clinica?.id ?? perfil.clinicaId ?? '',
        entityId: perfil.id,
      });
      const { data } = await api.post(`/api/storage/upload?${params}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setWebConfig(c => ({ ...c, fondoHeroUrl: data.url }));
      toast.success('Imagen subida');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Error al subir la imagen'));
    } finally {
      setUploadingHero(false);
      // Limpiar para permitir volver a subir el mismo archivo
      if (heroFileRef.current) heroFileRef.current.value = '';
    }
  }

  const tags = form.subespecialidades.split(',').map(s => s.trim()).filter(Boolean);
  const idiomasList = form.idiomas.split(',').map(s => s.trim()).filter(Boolean);
  const slugYaGuardado = !!perfil?.perfilPublicoSlug;

  // Medico mock para el preview en vivo (combina perfil cargado + form actual)
  const medicoPreview = perfil
    ? {
        ...perfil,
        bio: form.bio || perfil.bio,
        aniosExperiencia: form.aniosExperiencia ? parseInt(form.aniosExperiencia) : perfil.aniosExperiencia,
        subespecialidades: form.subespecialidades || perfil.subespecialidades,
        idiomas: form.idiomas || perfil.idiomas,
      }
    : null;

  if (isLoading) {
    return (
      <div className="-m-5 flex items-center justify-center" style={{ height: 'calc(100% + 40px)' }}>
        <p className="text-text-secondary text-sm">Cargando perfil...</p>
      </div>
    );
  }

  return (
    <div className="-m-5 flex flex-col" style={{ height: 'calc(100% + 40px)' }}>

      {/* ── Header ── */}
      <div className="flex-shrink-0 px-4 sm:px-6 pt-5 pb-0 bg-white">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-4">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-text-primary">Mi perfil público</h2>
            <p className="text-sm text-text-secondary mt-0.5">
              Gestiona cómo te presentas en la red médica y en internet
            </p>
          </div>
          <div className="flex items-center gap-2">
            {perfil?.perfilPublicoSlug && (
              <a
                href={`/dr/${perfil.perfilPublicoSlug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 border border-primary-200 px-3 py-2 rounded-lg hover:bg-primary-50 transition-colors"
              >
                <ExternalLink size={14} />
                Ver sitio público
              </a>
            )}
            {tab === 'web' && (
              <button
                onClick={handleGuardarWeb}
                disabled={updatePerfilMutation.isPending}
                className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-60 transition-colors"
              >
                {updatePerfilMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Publicar sitio
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit mb-0">
          {([['red', 'Red Médica'], ['web', 'Sitio Web']] as const).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                tab === key
                  ? 'bg-white text-text-primary shadow-sm'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Contenido de tabs ── */}
      <div className="flex-1 min-h-0 overflow-hidden">

        {/* ─── TAB RED MÉDICA ─────────────────────────────────────────────── */}
        {tab === 'red' && (
          <div className="h-full overflow-y-auto px-4 sm:px-6 py-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Formulario */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="font-semibold text-text-primary">Datos del perfil</h3>
                  {!editando && (
                    <button
                      onClick={() => setEditando(true)}
                      className="flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700"
                    >
                      <Edit3 size={14} /> Editar
                    </button>
                  )}
                </div>

                <form onSubmit={handleGuardarDatos} className="space-y-4">
                  {/* Slug */}
                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">
                      URL pública (slug)
                      {slugYaGuardado && (
                        <span className="ml-2 inline-flex items-center gap-1 text-amber-600">
                          <Lock size={10} />
                          No modificable
                        </span>
                      )}
                    </label>
                    <div className="flex items-center">
                      <span className="border border-r-0 border-gray-200 bg-gray-50 px-3 py-2.5 text-xs text-text-secondary rounded-l-xl whitespace-nowrap">
                        /dr/
                      </span>
                      <input
                        type="text"
                        className={`flex-1 border border-gray-200 rounded-r-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 ${
                          slugYaGuardado ? 'bg-gray-50 text-text-secondary cursor-not-allowed' : ''
                        }`}
                        placeholder="juan-perez-cardiologo"
                        value={form.perfilPublicoSlug}
                        onChange={e =>
                          !slugYaGuardado &&
                          setForm(f => ({ ...f, perfilPublicoSlug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))
                        }
                        readOnly={slugYaGuardado}
                        disabled={!editando && !slugYaGuardado}
                      />
                    </div>
                    {!slugYaGuardado && editando && (
                      <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                        <Lock size={10} />
                        Una vez guardado no podrá cambiarse
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">
                      <BookOpen size={12} className="inline mr-1" />Presentación / Bio
                    </label>
                    <textarea
                      rows={3}
                      className={`${inputCls} resize-none`}
                      placeholder="Cuéntanos sobre tu trayectoria y enfoque..."
                      value={form.bio}
                      onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
                      disabled={!editando}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">
                      <Award size={12} className="inline mr-1" />Años de experiencia
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={80}
                      className={inputCls}
                      placeholder="Ej. 12"
                      value={form.aniosExperiencia}
                      onChange={e => setForm(f => ({ ...f, aniosExperiencia: e.target.value }))}
                      disabled={!editando}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">
                      <Stethoscope size={12} className="inline mr-1" />Subespecialidades (separadas por coma)
                    </label>
                    <input
                      type="text"
                      className={inputCls}
                      placeholder="Ej. Cardiología pediátrica, Ecocardiografía"
                      value={form.subespecialidades}
                      onChange={e => setForm(f => ({ ...f, subespecialidades: e.target.value }))}
                      disabled={!editando}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">
                      <Languages size={12} className="inline mr-1" />Idiomas (separados por coma)
                    </label>
                    <input
                      type="text"
                      className={inputCls}
                      placeholder="Ej. Español, Inglés, Francés"
                      value={form.idiomas}
                      onChange={e => setForm(f => ({ ...f, idiomas: e.target.value }))}
                      disabled={!editando}
                    />
                  </div>

                  {editando && (
                    <div className="flex gap-3 pt-2">
                      <button
                        type="button"
                        onClick={() => setEditando(false)}
                        className="flex-1 border border-gray-200 text-text-secondary py-2.5 rounded-xl text-sm hover:bg-gray-50"
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        disabled={updatePerfilMutation.isPending}
                        className="flex-1 flex items-center justify-center gap-2 bg-primary-600 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-primary-700 disabled:opacity-60"
                      >
                        {updatePerfilMutation.isPending
                          ? <Loader2 size={14} className="animate-spin" />
                          : <Check size={14} />}
                        Guardar
                      </button>
                    </div>
                  )}
                </form>
              </div>

              {/* Vista previa de Red Médica */}
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden self-start">
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <Eye size={14} className="text-text-secondary" />
                    <h3 className="text-sm font-semibold text-text-primary">Vista previa</h3>
                  </div>
                  <span className="text-xs text-text-secondary bg-gray-100 px-2 py-0.5 rounded-full">Red Médica</span>
                </div>

                <div className="p-5">
                  <div className="bg-gradient-to-br from-primary-50 to-blue-50 rounded-xl p-4 mb-4">
                    <div className="flex items-start gap-3">
                      {perfil?.usuario?.fotoUrl ? (
                        <img src={perfil.usuario.fotoUrl} alt={perfil.nombre}
                          className="w-16 h-16 rounded-xl object-cover" />
                      ) : (
                        <div className="w-16 h-16 rounded-xl bg-primary-600 flex items-center justify-center text-white font-bold text-lg">
                          {getInitials(perfil?.nombre ?? 'M')}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-text-primary">Dr. {perfil?.nombre}</p>
                        {perfil?.especialidad?.nombre && (
                          <p className="text-sm text-primary-600 mt-0.5">{perfil.especialidad.nombre}</p>
                        )}
                        {form.aniosExperiencia && (
                          <div className="flex items-center gap-1 mt-1.5">
                            <Award size={12} className="text-amber-500" />
                            <span className="text-xs text-text-secondary">{form.aniosExperiencia} años de exp.</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {form.bio && (
                    <p className="text-sm text-text-secondary leading-relaxed mb-3 italic">"{form.bio}"</p>
                  )}

                  {tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {tags.map(tag => (
                        <span key={tag} className="px-2 py-0.5 bg-primary-50 text-primary-600 rounded-full text-xs">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {idiomasList.length > 0 && (
                    <div className="flex items-center gap-2 mb-3">
                      <Languages size={12} className="text-gray-400" />
                      <span className="text-xs text-text-secondary">{idiomasList.join(' · ')}</span>
                    </div>
                  )}

                  {perfil?.clinica && (
                    <div className="bg-gray-50 rounded-xl p-3 space-y-1">
                      <p className="text-xs font-semibold text-text-primary">{perfil.clinica.nombre}</p>
                      {(perfil.clinica.ciudad || perfil.clinica.estado) && (
                        <div className="flex items-center gap-1">
                          <MapPin size={10} className="text-gray-400" />
                          <span className="text-xs text-text-secondary">
                            {[perfil.clinica.ciudad, perfil.clinica.estado].filter(Boolean).join(', ')}
                          </span>
                        </div>
                      )}
                      {perfil?.cedulaProfesional && (
                        <p className="text-xs text-text-secondary">Cédula: {perfil.cedulaProfesional}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ─── TAB SITIO WEB ──────────────────────────────────────────────── */}
        {tab === 'web' && (
          <div className="h-full flex flex-col md:flex-row overflow-hidden">

            {/* ── Panel lateral de controles ── */}
            <div className="w-full md:w-80 flex-shrink-0 flex flex-col border-b md:border-b-0 md:border-r border-gray-100 bg-white overflow-y-auto flex-1 md:flex-none">

              {/* Aviso URL */}
              <div className="flex-shrink-0 px-4 py-3 border-b border-gray-100 bg-gray-50 text-xs text-text-secondary">
                {perfil?.perfilPublicoSlug ? (
                  <span>URL: <span className="font-mono text-primary-600">/dr/{perfil.perfilPublicoSlug}</span></span>
                ) : (
                  <span className="text-amber-600">Configura el slug en "Red Médica" para activar la URL</span>
                )}
              </div>

              <div className="flex-1 px-4 py-4 space-y-6">

                {/* Color de acento */}
                <div>
                  <label className="flex items-center gap-2 text-xs font-semibold text-text-primary mb-2">
                    <Palette size={14} className="text-primary-500" />
                    Color de marca
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={webConfig.colorAcento}
                      onChange={e => setWebConfig(c => ({ ...c, colorAcento: e.target.value }))}
                      className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer p-0.5 bg-white"
                    />
                    <input
                      type="text"
                      value={webConfig.colorAcento}
                      onChange={e => {
                        const v = e.target.value;
                        if (/^#[0-9a-fA-F]{0,6}$/.test(v)) setWebConfig(c => ({ ...c, colorAcento: v }));
                      }}
                      className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-300"
                      maxLength={7}
                    />
                  </div>
                </div>

                {/* Tipografía */}
                <div>
                  <label className="flex items-center gap-2 text-xs font-semibold text-text-primary mb-2">
                    <Type size={14} className="text-primary-500" />
                    Tipografía
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {(Object.entries(TIPOGRAFIAS) as [TipografiaKey, typeof TIPOGRAFIAS.moderna][]).map(([key, tip]) => (
                      <button
                        key={key}
                        onClick={() => setWebConfig(c => ({ ...c, tipografia: key }))}
                        className={`px-3 py-2.5 rounded-xl border text-left transition-all ${
                          webConfig.tipografia === key
                            ? 'border-primary-400 bg-primary-50 text-primary-700'
                            : 'border-gray-200 hover:border-gray-300 text-text-secondary'
                        }`}
                      >
                        <p className="text-xs font-semibold">{tip.label}</p>
                        <p className="text-[10px] text-text-secondary mt-0.5 truncate" style={{ fontFamily: tip.heading }}>
                          Aa Bb Cc
                        </p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Fondo del hero */}
                <div>
                  <label className="flex items-center gap-2 text-xs font-semibold text-text-primary mb-2">
                    <ImageIcon size={14} className="text-primary-500" />
                    Imagen de fondo del hero
                  </label>
                  <input
                    type="file"
                    ref={heroFileRef}
                    accept="image/*"
                    className="hidden"
                    onChange={handleHeroUpload}
                  />
                  {webConfig.fondoHeroUrl ? (
                    <div className="relative rounded-xl overflow-hidden border border-gray-200">
                      <img src={webConfig.fondoHeroUrl} alt="Fondo hero" className="w-full h-24 object-cover" />
                      <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/40 opacity-0 hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => heroFileRef.current?.click()}
                          disabled={uploadingHero}
                          className="bg-white text-gray-800 text-xs px-3 py-1.5 rounded-lg font-medium"
                        >
                          Cambiar
                        </button>
                        <button
                          onClick={() => setWebConfig(c => ({ ...c, fondoHeroUrl: undefined }))}
                          className="bg-white text-red-600 text-xs px-3 py-1.5 rounded-lg font-medium"
                        >
                          Quitar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => heroFileRef.current?.click()}
                      disabled={uploadingHero}
                      className="w-full flex flex-col items-center gap-2 border-2 border-dashed border-gray-200 rounded-xl py-4 text-text-secondary hover:border-primary-300 hover:text-primary-600 hover:bg-primary-50 transition-colors disabled:opacity-60"
                    >
                      {uploadingHero
                        ? <Loader2 size={20} className="animate-spin" />
                        : <Upload size={20} />}
                      <span className="text-xs">{uploadingHero ? 'Subiendo...' : 'Subir imagen'}</span>
                    </button>
                  )}
                  <p className="text-[11px] text-text-secondary mt-1">
                    Si no hay imagen se usa un gradiente con el color de marca
                  </p>
                </div>

                {/* Texto adicional */}
                <div>
                  <label className="flex items-center gap-2 text-xs font-semibold text-text-primary mb-2">
                    <AlignLeft size={14} className="text-primary-500" />
                    Texto adicional
                  </label>
                  <textarea
                    rows={3}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-300"
                    placeholder="Horarios, información de seguros, mensaje especial..."
                    value={webConfig.textoAdicional ?? ''}
                    onChange={e => setWebConfig(c => ({ ...c, textoAdicional: e.target.value }))}
                  />
                </div>

                {/* CTA */}
                <div>
                  <label className="flex items-center gap-2 text-xs font-semibold text-text-primary mb-2">
                    <LinkIcon size={14} className="text-primary-500" />
                    Botón de llamada a la acción
                  </label>
                  <div className="space-y-2">
                    {/* Texto del botón */}
                    <input
                      type="text"
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                      placeholder="Texto del botón (Ej. Agendar cita)"
                      value={webConfig.ctaTexto ?? ''}
                      onChange={e => setWebConfig(c => ({ ...c, ctaTexto: e.target.value }))}
                    />

                    {/* Selector de tipo */}
                    {(() => {
                      const tipos: { key: CtaTipo; label: string; icon: React.ReactNode; placeholder: string }[] = [
                        { key: 'tel',  label: 'Teléfono',  icon: <Phone size={12} />,          placeholder: '+52 55 1234 5678' },
                        { key: 'wa',   label: 'WhatsApp',  icon: <MessageCircle size={12} />,   placeholder: '5215512345678' },
                        { key: 'mail', label: 'Correo',    icon: <MailIcon size={12} />,        placeholder: 'doctor@clinica.com' },
                        { key: 'url',  label: 'Enlace',    icon: <LinkIcon size={12} />,        placeholder: 'https://miagenda.com' },
                      ];
                      const activeTipo = tipos.find(t => t.key === ctaTipo)!;
                      return (
                        <>
                          <div className="grid grid-cols-4 gap-1 bg-gray-100 rounded-xl p-1">
                            {tipos.map(t => (
                              <button
                                key={t.key}
                                type="button"
                                onClick={() => {
                                  setCtaTipo(t.key);
                                  setCtaValor('');
                                  setWebConfig(c => ({ ...c, ctaUrl: '' }));
                                }}
                                className={`flex flex-col items-center gap-0.5 py-1.5 rounded-lg text-[10px] font-medium transition-all ${
                                  ctaTipo === t.key
                                    ? 'bg-white text-primary-600 shadow-sm'
                                    : 'text-text-secondary hover:text-text-primary'
                                }`}
                              >
                                {t.icon}
                                {t.label}
                              </button>
                            ))}
                          </div>
                          <input
                            type={ctaTipo === 'mail' ? 'email' : ctaTipo === 'url' ? 'url' : 'tel'}
                            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                            placeholder={activeTipo.placeholder}
                            value={ctaValor}
                            onChange={e => {
                              const v = e.target.value;
                              setCtaValor(v);
                              setWebConfig(c => ({ ...c, ctaUrl: buildCtaUrl(ctaTipo, v) }));
                            }}
                          />
                          {ctaTipo === 'wa' && ctaValor && (
                            <p className="text-[11px] text-text-secondary">
                              Solo números con código de país, sin + ni espacios
                            </p>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>

              </div>

              {/* Guardar al fondo del panel */}
              <div className="flex-shrink-0 px-4 py-4 border-t border-gray-100">
                <button
                  onClick={handleGuardarWeb}
                  disabled={updatePerfilMutation.isPending}
                  className="w-full flex items-center justify-center gap-2 bg-primary-600 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-primary-700 disabled:opacity-60 transition-colors"
                >
                  {updatePerfilMutation.isPending
                    ? <Loader2 size={14} className="animate-spin" />
                    : <Save size={14} />}
                  Publicar sitio
                </button>
              </div>
            </div>

            {/* ── Preview en vivo (solo desktop) ── */}
            <div className="hidden md:flex flex-1 flex-col bg-gray-100 min-w-0 overflow-hidden">
              {/* Chrome del navegador simulado */}
              <div className="flex-shrink-0 flex items-center gap-2 px-4 py-2.5 bg-gray-200 border-b border-gray-300">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-yellow-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                </div>
                <div className="flex-1 bg-white rounded-md px-3 py-1 text-xs text-gray-500 font-mono truncate">
                  {perfil?.perfilPublicoSlug
                    ? `anik.app/dr/${perfil.perfilPublicoSlug}`
                    : 'anik.app/dr/tu-slug'}
                </div>
                <Eye size={14} className="text-gray-500 flex-shrink-0" />
              </div>

              {/* Contenido del preview */}
              <div className="flex-1 overflow-y-auto">
                {medicoPreview ? (
                  <WebTemplatePreview medico={medicoPreview} config={webConfig} isPreview={false} />
                ) : (
                  <div className="flex items-center justify-center h-full text-text-secondary text-sm">
                    Sin datos de perfil
                  </div>
                )}
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
}
